import { motion } from "framer-motion";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import {
  Landmark,
  Gavel,
  FileSignature,
  FileText,
  Building2,
  Handshake,
  Copyright,
  BookOpen,
  Coins,
} from "lucide-react";

const AREAS = [
  {
    id: "tax-laws",
    icon: Landmark,
    title: "Tax Laws",
    span: "lg:col-span-4",
    featured: true,
    body: "Full-spectrum tax counsel — corporate taxation, import duties, sales tax, stamp duty, VAT and related disputes. Anchored by a Master's in Tax & Customs Administration.",
  },
  {
    id: "litigation",
    icon: Gavel,
    title: "Litigation",
    span: "lg:col-span-2",
    body: "Commercial, civil, criminal, employment, land and family matters — from filing to defence, settlement and appeal.",
  },
  {
    id: "conveyance",
    icon: FileSignature,
    title: "Conveyance",
    span: "lg:col-span-2",
    body: "Land transactions, sale agreements, leases and tenancy documentation.",
  },
  {
    id: "contracts",
    icon: FileText,
    title: "Contracts",
    span: "lg:col-span-2",
    body: "Drafting, review and viability advice — joint ventures, franchises, employment and construction agreements.",
  },
  {
    id: "company-formation",
    icon: Building2,
    title: "Company Formation & Investment",
    span: "lg:col-span-2",
    body: "Incorporation, directorship changes, share transfers and investment counsel for local and foreign investors.",
  },
  {
    id: "arbitration",
    icon: Handshake,
    title: "Arbitration",
    span: "lg:col-span-3",
    body: "Consultancy and representation of parties before arbitral tribunals.",
  },
  {
    id: "intellectual-property",
    icon: Copyright,
    title: "Intellectual Property",
    span: "lg:col-span-3",
    body: "Registration and enforcement — patents, trademarks, copyrights, industrial designs and geographical indications.",
  },
  {
    id: "consultancy",
    icon: BookOpen,
    title: "General Consultancy",
    span: "lg:col-span-3",
    body: "Opinions, reports and structured legal updates across a breadth of client issues.",
  },
  {
    id: "debt-collection",
    icon: Coins,
    title: "Debt Collection",
    span: "lg:col-span-3",
    body: "Demand letters to execution — court proceedings, insolvency and receivership, with monthly recovery reports and prompt remittance of recovered sums.",
  },
];

export default function PracticeAreas() {
  return (
    <section
      id="practice"
      data-testid="practice-section"
      className="relative py-28 lg:py-40 bg-navy-900/40"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <FadeUp>
          <SectionLabel>Areas of Specialization</SectionLabel>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-2xl leading-tight">
              Nine Disciplines. <span className="text-gold">One Standard.</span>
            </h2>
            <p className="text-ink-dim max-w-md text-sm sm:text-base leading-relaxed">
              From the tax tribunal to the conveyancer's desk — counsel built
              around the matter, not the clock.
            </p>
          </div>
        </FadeUp>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {AREAS.map((a, i) => (
            <motion.article
              key={a.id}
              data-testid={`practice-area-card-${a.id}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -6 }}
              className={`sheen-target group relative overflow-hidden rounded-3xl border border-gold/15 bg-navy-700/60 p-8 hover:border-gold/50 transition-colors duration-500 ${a.span}`}
            >
              {a.featured && (
                <>
                  <img
                    src="https://images.pexels.com/photos/6077181/pexels-photo-6077181.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover opacity-15 group-hover:opacity-25 transition-opacity duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/70 to-transparent" />
                </>
              )}
              <div className="relative">
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-gold/25 bg-navy-950/60 text-gold group-hover:bg-gold group-hover:text-navy-950 transition-colors duration-500">
                    <a.icon size={20} />
                  </span>
                  <span className="font-mono text-xs text-ink-muted">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className={`font-display mt-6 ${a.featured ? "text-2xl" : "text-lg"}`}>
                  {a.title}
                </h3>
                <p className="mt-3 text-sm text-ink-dim leading-relaxed">{a.body}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
